import { ConversionBlock } from '@/entities/conversion'
import { EnrichedPaymentRequisiteModel, PaymentRequisitIcon } from '@/entities/paymentRequisit'
import { RequisiteModel } from '@/features/requisites'
import PauseIcon from '@/shared/assets/icons/pause.svg?react'
import PlayIcon from '@/shared/assets/icons/play.svg?react'
import TrashIcon from '@/shared/assets/icons/trash.svg?react'
import { BaseButton, BaseCheckbox, BaseProgressBar } from '@/shared/components'
import { formatterRubWithCurrency } from '@/shared/utils'
import { ColumnDef } from '@tanstack/react-table'
import { RequisitVerify } from '@/entities/requisit/ui/RequisitVerify.tsx'
import { formatRequisite } from '@/shared/lib'

interface PaymentRequisitsProps {
  onDisable?: (id: string) => void
  onEnable?: (id: string) => void
  onDelete?: (id: string, name: string) => void
  showSelectColumn?: boolean
  maxDailyLimit?: number
  requesit?: RequisiteModel
}

export const paymentRequisitsColumns = ({
  onDisable,
  onEnable,
  onDelete,
  showSelectColumn = false,
  maxDailyLimit,
  requesit,
}: PaymentRequisitsProps): ColumnDef<EnrichedPaymentRequisiteModel>[] => {
  const baseColumns: ColumnDef<EnrichedPaymentRequisiteModel>[] = [
    {
      accessorKey: 'detail',
      header: 'Реквизит',
      cell: ({ row }) => {
        const requisit = row.original
        const paymentRequisit = formatRequisite(requisit?.value ?? '')
        return (
          <div className="flex gap-3">
            <PaymentRequisitIcon
              accountType={requisit.requisitType}
              status={!(requisit.isDisabledByUser || requisit.isBlocked) ? 1 : 40}
              bg
            />
            <div className="flex items-center">
              <span className="font-medium">{paymentRequisit}</span>
            </div>
          </div>
        )
      },
    },
    {
      id: 'maxDailyLimit',
      accessorKey: 'maxDailyLimit',
      header: 'Суточный лимит',
      cell: ({ row }) => {
        return (
          <BaseProgressBar
            variant="table"
            currentBalance={row.original.dailyStats?.amountInRub ?? 0}
            totalBalance={maxDailyLimit ?? 0}
          />
        )
      },
    },
    {
      id: 'totalTurnover',
      accessorKey: 'totalTurnover',
      header: 'Общий оборот',
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {formatterRubWithCurrency.format(row.original.totalStats?.amountInRub ?? 0)}
          </div>
        )
      },
    },
    {
      id: 'conversion',
      accessorKey: 'conversion',
      header: 'Успешные сделки',
      cell: ({ row }) => {
        return (
          <ConversionBlock
            current={row.original.totalStats?.counterInSuccess ?? 0}
            goal={row.original.totalStats?.counterIn ?? 0}
            variant="table"
          />
        )
      },
    },
    // Новый столбец для верификации (без заголовка)
    {
      id: 'verification',
      header: '',
      cell: ({ row }) => {
        const paymentRequisit = row.original
        if (!requesit) return <></>
        return <RequisitVerify paymentRequisit={paymentRequisit} requesit={requesit} />
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        return (
          <div className="flex gap-3">
            {onEnable && onDisable && (
              <BaseButton
                variant="ghost"
                size="icon"
                icon={
                  row.original.isDisabledByUser ? (
                    <PlayIcon className="text-primary dark:text-primary-dark" />
                  ) : (
                    <PauseIcon className="text-primary dark:text-primary-dark" />
                  )
                }
                onClick={() =>
                  row.original.isDisabledByUser
                    ? onEnable?.(row.original.id ?? '')
                    : onDisable?.(row.original.id ?? '')
                }
              ></BaseButton>
            )}
            {onDelete && (
              <BaseButton
                variant="ghost"
                size="icon"
                onClick={() =>
                  onDelete?.(row.original.id ?? '', `*${row.original.value.slice(-4)}`)
                }
              >
                <TrashIcon className="text-error" />
              </BaseButton>
            )}
          </div>
        )
      },
    },
  ]

  if (showSelectColumn) {
    return [
      {
        id: 'select',
        accessorKey: 'select',
        header: () => (
          <div
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
            }}
          >
            {/* <BaseCheckbox
              checked={table.getIsAllPageRowsSelected()}
              onChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            /> */}
          </div>
        ),
        cell: ({ row }) => (
          <div
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
            }}
          >
            <BaseCheckbox
              checked={row.getIsSelected()}
              onChange={(value) => row.toggleSelected(value)}
            />
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
      },
      ...baseColumns,
    ]
  }

  return baseColumns
}
